import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, InputNumber, Space, Typography, Alert, message } from 'antd';
import { getMqttMappingByMeter, mqttPublishByMeter, getMqttLatestMessage } from '../api';
import {
  getAdminMeterPublishKey,
  remotePayloadUseBleDirect,
  buildFinalPayload,
  getRxAddressCandidates,
  buildMqttMessagePollOpts,
} from '../utils/yjMeterRemote';

const { Text } = Typography;

const NESTED_DIALOG_Z_INDEX = 2100;
const POLL_MS_DEFAULT = 10000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const emptyTrace = () => ({ tx: '', rx: '' });

function assertRemoteAddress12(meterKey, isBleDirect) {
  const m = String(meterKey || '').trim().toLowerCase();
  if (isBleDirect) return m;
  if (!/^[0-9a-f]{12}$/.test(m)) {
    throw new Error('远程 55 帧需 12 位十六进制地址（4G 多为 device_mac 去冒号），请检查设备档案');
  }
  return m;
}

function buildReadMeteringFactorRawNoCrc(publishKey, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  return ((isBleDirect ? '' : m) + '55' + '02' + '02' + '00000000').toLowerCase();
}

function buildSetMeteringFactorRawNoCrc(publishKey, displayValue, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  const scaled = Math.round(Number(displayValue) * 1000);
  if (scaled < 0 || scaled > 0xffffffff) throw new Error('数值超出范围');
  const valueHex = (scaled >>> 0).toString(16).padStart(8, '0');
  return ((isBleDirect ? '' : m) + '55' + '02' + 'c2' + valueHex).toLowerCase();
}

/** 保存 ID=62：读 0x3E，写 0xFE（与 02→c2 相同：OPTION|0xC0）；旧 0x7E=读+禁止，无法落盘 */
const SAVE_PARAM_OPTION_WRITE = 'fe';

function buildSaveParamsRawNoCrc(publishKey, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  return ((isBleDirect ? '' : m) + '55' + '02' + SAVE_PARAM_OPTION_WRITE + '00000000').toLowerCase();
}

function parse55MeteringFactorResponse(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 26) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '55' || msg.substring(14, 16) !== '02') return null;
  const subCmd = msg.substring(16, 18);
  if (subCmd !== '02' && subCmd !== 'c2') return null;
  const payloadNoCrc = msg.length > 2 ? msg.substring(0, msg.length - 2) : msg;
  const valueHex = payloadNoCrc.length >= 8 ? payloadNoCrc.substring(payloadNoCrc.length - 8) : '00000000';
  const valueDec = parseInt(valueHex || '0', 16) || 0;
  const valueFloat = Number((valueDec / 1000).toFixed(3));
  return { subCmd, valueHex, valueFloat, raw: msg };
}

function parse55SaveConfirm(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 26) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '55' || msg.substring(14, 16) !== '02') return null;
  const subCmd = msg.substring(16, 18);
  const subDec = parseInt(subCmd, 16);
  if (
    subCmd === SAVE_PARAM_OPTION_WRITE ||
    subCmd === '7e' ||
    subCmd === 'be' ||
    (Number.isFinite(subDec) && (subDec & 0x3f) === 0x3e)
  ) {
    const payloadNoCrc = msg.length > 2 ? msg.substring(0, msg.length - 2) : msg;
    const valueHex = payloadNoCrc.length >= 8 ? payloadNoCrc.substring(payloadNoCrc.length - 8) : '00000000';
    return { subCmd, valueHex, raw: msg };
  }
  return null;
}

function parse55C2SetConfirm(rawMsg, addressCandidates) {
  const parsed = parse55MeteringFactorResponse(rawMsg, addressCandidates);
  if (parsed && parsed.subCmd === 'c2') return parsed;
  return null;
}

function HexTrace({ tx, rx, extra }) {
  if (!tx && !rx && !extra) return null;
  const lineStyle = { marginTop: 4, fontSize: 12, wordBreak: 'break-all' };
  return (
    <div style={{ marginTop: 8 }}>
      {tx ? (
        <div style={lineStyle}>
          <Text type="secondary">下发 </Text>
          <Text code>{tx}</Text>
        </div>
      ) : null}
      {rx ? (
        <div style={lineStyle}>
          <Text type="secondary">回包 </Text>
          <Text code>{rx}</Text>
        </div>
      ) : null}
      {extra ? (
        <div style={{ ...lineStyle, marginTop: 6 }}>
          <Text type="secondary">{extra}</Text>
        </div>
      ) : null}
    </div>
  );
}

const MeterMeteringFactorModal = ({ open, device, variant, onClose }) => {
  const [readLoading, setReadLoading] = useState(false);
  const [readValue, setReadValue] = useState(null);
  const [readError, setReadError] = useState('');
  const [readTrace, setReadTrace] = useState(emptyTrace);

  const [coeffInput, setCoeffInput] = useState(null);
  const [setLoading, setSetLoading] = useState(false);
  const [setError, setSetError] = useState('');
  const [setTrace, setSetTrace] = useState(emptyTrace);

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveTrace, setSaveTrace] = useState(emptyTrace);
  const [saveValueHex, setSaveValueHex] = useState('');

  const deviceKind = variant === 'water' ? '水表' : '电表';

  const resetState = useCallback(() => {
    setReadLoading(false);
    setReadValue(null);
    setReadError('');
    setReadTrace(emptyTrace());
    setCoeffInput(null);
    setSetLoading(false);
    setSetError('');
    setSetTrace(emptyTrace());
    setSaveLoading(false);
    setSaveError('');
    setSaveTrace(emptyTrace());
    setSaveValueHex('');
  }, []);

  useEffect(() => {
    if (open) resetState();
  }, [open, device?.id, resetState]);

  const pollRx = async (rxTopic, candidates, pollOpts, parseFn, timeoutMs = POLL_MS_DEFAULT) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const msgRes = await getMqttLatestMessage(rxTopic, 'raw', pollOpts);
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      if (raw) {
        const parsed = parseFn(String(raw), candidates);
        if (parsed) return parsed;
      }
      await sleep(400);
    }
    return null;
  };

  const handleReadMeteringFactor = async () => {
    if (!device) return;
    setReadLoading(true);
    setReadError('');
    setReadTrace(emptyTrace());
    setReadValue(null);
    const publishKey = getAdminMeterPublishKey(device);
    if (!publishKey) {
      setReadLoading(false);
      setReadError('无法确定表号或 MAC，请检查设备档案');
      return;
    }

    const readCore = async () => {
      const mapRes = await getMqttMappingByMeter(publishKey);
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildReadMeteringFactorRawNoCrc(publishKey, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      const candidates = getRxAddressCandidates(device);
      const pollOpts = buildMqttMessagePollOpts(publishKey, 'param55', publishAt);
      const parsed = await pollRx(rxTopic, candidates, pollOpts, parse55MeteringFactorResponse);
      return { payload, parsed };
    };

    try {
      let result = await readCore();
      if (!result?.parsed) {
        setReadLoading(false);
        Modal.confirm({
          zIndex: NESTED_DIALOG_Z_INDEX,
          title: '读取超时',
          content: '是否重试？',
          okText: '重试',
          cancelText: '取消',
          onOk: async () => {
            setReadLoading(true);
            setReadError('');
            try {
              result = await readCore();
              if (!result?.parsed) {
                setReadError('读取超时，未收到回包');
                return;
              }
              setReadTrace({ tx: result.payload, rx: result.parsed.raw });
              setReadValue(result.parsed.valueFloat);
            } catch (e2) {
              setReadError(e2?.response?.data?.msg || e2?.message || '读取失败');
            } finally {
              setReadLoading(false);
            }
          },
        });
        return;
      }
      setReadTrace({ tx: result.payload, rx: result.parsed.raw });
      setReadValue(result.parsed.valueFloat);
    } catch (e) {
      setReadError(e?.response?.data?.msg || e?.response?.data?.error || e?.message || '读取失败');
    } finally {
      setReadLoading(false);
    }
  };

  const handleSetMeteringFactor = async () => {
    if (!device) return;
    const v = Number(coeffInput);
    if (coeffInput == null || Number.isNaN(v) || v < 0) {
      message.warning('请输入有效非负数值');
      return;
    }
    setSetLoading(true);
    setSetError('');
    setSetTrace(emptyTrace());
    const publishKey = getAdminMeterPublishKey(device);
    if (!publishKey) {
      setSetLoading(false);
      setSetError('无法确定表号或 MAC，请检查设备档案');
      return;
    }

    const setCore = async () => {
      const mapRes = await getMqttMappingByMeter(publishKey);
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const candidates = getRxAddressCandidates(device);
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildSetMeteringFactorRawNoCrc(publishKey, v, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      const pollOpts = buildMqttMessagePollOpts(publishKey, 'param55', publishAt);
      const parsed = await pollRx(rxTopic, candidates, pollOpts, parse55C2SetConfirm);
      return { payload, parsed };
    };

    const applySet = (result) => {
      setSetTrace({ tx: result.payload, rx: result.parsed.raw });
      setReadValue(result.parsed.valueFloat);
    };

    try {
      let result = await setCore();
      if (!result?.parsed) {
        setSetLoading(false);
        Modal.confirm({
          zIndex: NESTED_DIALOG_Z_INDEX,
          title: '设置超时',
          content: '是否重试？',
          okText: '重试',
          cancelText: '取消',
          onOk: async () => {
            setSetLoading(true);
            setSetError('');
            try {
              result = await setCore();
              if (!result?.parsed) {
                setSetError('设置超时，未收到 c2 回包');
                return;
              }
              applySet(result);
            } catch (ex) {
              setSetError(ex?.response?.data?.msg || ex?.message || '设置失败');
            } finally {
              setSetLoading(false);
            }
          },
        });
        return;
      }
      applySet(result);
    } catch (e) {
      setSetError(e?.response?.data?.msg || e?.response?.data?.error || e?.message || '设置失败');
    } finally {
      setSetLoading(false);
    }
  };

  const handleSaveParams = async () => {
    if (!device) return;
    setSaveLoading(true);
    setSaveError('');
    setSaveTrace(emptyTrace());
    setSaveValueHex('');
    const publishKey = getAdminMeterPublishKey(device);
    if (!publishKey) {
      setSaveLoading(false);
      setSaveError('无法确定表号或 MAC，请检查设备档案');
      return;
    }

    const saveCore = async () => {
      const mapRes = await getMqttMappingByMeter(publishKey);
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildSaveParamsRawNoCrc(publishKey, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      const candidates = getRxAddressCandidates(device);
      const pollOpts = buildMqttMessagePollOpts(publishKey, 'param55', publishAt);
      const parsed = await pollRx(rxTopic, candidates, pollOpts, parse55SaveConfirm);
      return { payload, parsed };
    };

    const applySave = (result) => {
      setSaveTrace({ tx: result.payload, rx: result.parsed.raw });
      setSaveValueHex(result.parsed.valueHex);
    };

    try {
      let result = await saveCore();
      if (!result?.parsed) {
        setSaveLoading(false);
        Modal.confirm({
          zIndex: NESTED_DIALOG_Z_INDEX,
          title: '保存超时',
          content: '是否重试？',
          okText: '重试',
          cancelText: '取消',
          onOk: async () => {
            setSaveLoading(true);
            setSaveError('');
            try {
              result = await saveCore();
              if (!result?.parsed) {
                setSaveError('保存超时，未收到回包');
                return;
              }
              applySave(result);
            } catch (ex) {
              setSaveError(ex?.response?.data?.msg || ex?.message || '指令发送异常');
            } finally {
              setSaveLoading(false);
            }
          },
        });
        return;
      }
      applySave(result);
    } catch (e) {
      setSaveError(e?.response?.data?.msg || e?.response?.data?.error || e?.message || '指令发送异常');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Modal
      title={`计量系数 — ${device?.current_meter_no || device?.id || ''}（${deviceKind}）`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
      ]}
      width={580}
      zIndex={1000}
      destroyOnClose
      maskClosable={false}
    >
      {device && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>读取计量系数</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">解析值（÷1000）</Text>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>
                {readValue != null && !Number.isNaN(readValue) ? readValue.toFixed(3) : '—'}
              </div>
            </div>
            {readError && <Alert type="error" message={readError} showIcon style={{ marginTop: 8 }} />}
            <HexTrace tx={readTrace.tx} rx={readTrace.rx} />
            <Button type="primary" style={{ marginTop: 10 }} loading={readLoading} onClick={handleReadMeteringFactor}>
              读取
            </Button>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text strong>设置计量系数</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap align="center">
                <InputNumber
                  min={0}
                  step={0.001}
                  precision={3}
                  value={coeffInput}
                  onChange={setCoeffInput}
                  placeholder="如 1.000"
                  style={{ width: 140 }}
                />
                <Button type="primary" loading={setLoading} onClick={handleSetMeteringFactor}>
                  设置
                </Button>
              </Space>
            </div>
            {setError && <Alert type="error" message={setError} showIcon style={{ marginTop: 8 }} />}
            <HexTrace tx={setTrace.tx} rx={setTrace.rx} />
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text strong>保存参数</Text>
            {saveValueHex ? (
              <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600 }}>
                回包 VALUE：{saveValueHex}
              </div>
            ) : null}
            {saveError && <Alert type="error" message={saveError} showIcon style={{ marginTop: 8 }} />}
            <HexTrace tx={saveTrace.tx} rx={saveTrace.rx} />
            <Button style={{ marginTop: 10 }} loading={saveLoading} onClick={handleSaveParams}>
              保存
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default MeterMeteringFactorModal;
